import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Bell, Shield, DollarSign, AlertTriangle, Eye, CheckCheck, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 15000;

const priorityColor = (p) =>
  ({
    critical: "text-red-400 bg-red-500/10 border-red-500/40",
    high: "text-[#FF6BC5] bg-[#EC008C]/10 border-[#EC008C]/40",
    normal: "text-[#EC008C] bg-[#EC008C]/10 border-[#EC008C]/40",
  })[p] || "text-white bg-[#1A2030] border-[#1A2030]";

const kindIcon = (kind) => {
  if (kind === "security_threat") return <Shield className="w-3.5 h-3.5" />;
  if (kind === "leak_detection") return <AlertTriangle className="w-3.5 h-3.5" />;
  if (kind === "day11_wire_queued") return <DollarSign className="w-3.5 h-3.5" />;
  return <Bell className="w-3.5 h-3.5" />;
};

const timeAgo = (iso) => {
  try {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch {
    return "";
  }
};

/**
 * Top-corner in-app notification bell.
 *
 * - Polls /api/me/notifications/inbox every 15s.
 * - Renders a red dot + subtle blink animation whenever `total_unread > 0`.
 * - Clicking the bell opens a slide-out panel showing every unread item,
 *   grouped by admin vs. personal.
 * - Admins see security threats, Day-11 wire queue additions, and
 *   Leak Detector hits in real-time.
 */
const NotificationBell = () => {
  const { token, isAuthenticated } = useAuth();
  const [inbox, setInbox] = useState(null);
  const [open, setOpen] = useState(false);
  const [blink, setBlink] = useState(false);
  const lastCountRef = useRef(0);

  const fetchInbox = async () => {
    try {
      const res = await axios.get(`${API}/me/notifications/inbox?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const nextCount = res.data.total_unread || 0;
      if (nextCount > lastCountRef.current && lastCountRef.current !== 0) {
        // A NEW notification landed since the last poll — trigger a
        // one-shot blink so ops sees the change even if they weren't
        // watching the number.
        setBlink(true);
        setTimeout(() => setBlink(false), 4500);
      }
      lastCountRef.current = nextCount;
      setInbox(res.data);
    } catch {
      // Silent — poll error must never crash the app shell.
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !token) return undefined;
    fetchInbox();
    const t = setInterval(fetchInbox, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  const markAll = async (scope) => {
    try {
      await axios.post(
        `${API}/me/notifications/read`,
        { mark_all: true, scope },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchInbox();
    } catch {
      // ignore
    }
  };

  if (!isAuthenticated) return null;
  const unread = inbox?.total_unread || 0;
  const hasUnread = unread > 0;

  return (
    <>
      <button
        type="button"
        aria-label="Notifications"
        onClick={() => setOpen(true)}
        className={`relative rounded-full p-2 border transition-colors ${
          hasUnread
            ? "border-[#EC008C] text-[#EC008C] bg-[#EC008C]/10"
            : "border-[#1A2030] text-[#8A92A0] hover:text-white hover:border-[#EC008C]/60"
        } ${blink ? "sf-notification-blink" : ""}`}
        data-testid="notification-bell"
      >
        <Bell className="w-4 h-4" />
        {hasUnread && (
          <span
            className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EC008C] text-black text-[10px] font-bold flex items-center justify-center border border-black`}
            data-testid="notification-count"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex" data-testid="notification-panel">
          <div
            className="flex-1 bg-black/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <aside className="w-full sm:w-[420px] bg-[#050810] border-l border-[#1A2030] h-full overflow-y-auto">
            <header className="sticky top-0 z-10 bg-[#050810] border-b border-[#1A2030] px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#EC008C]" />
                <span className="font-['Unbounded'] text-lg font-bold text-white">Notifications</span>
                {hasUnread && (
                  <span className="ml-2 text-xs px-2 py-0.5 bg-[#EC008C] text-black rounded-full font-bold">
                    {unread}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 text-[#8A92A0] hover:text-white"
                data-testid="notification-close"
                aria-label="Close notifications"
              >
                <X className="w-5 h-5" />
              </button>
            </header>

            {inbox?.is_admin && (
              <section className="px-4 py-3 border-b border-[#1A2030]">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-purple-300" />
                    <span className="uppercase text-xs tracking-widest text-purple-300 font-bold">
                      Admin ({inbox.admin_unread || 0} unread)
                    </span>
                  </div>
                  {(inbox.admin_unread || 0) > 0 && (
                    <button
                      type="button"
                      onClick={() => markAll("admin")}
                      className="text-xs text-[#8A92A0] hover:text-white flex items-center gap-1"
                      data-testid="notification-markall-admin"
                    >
                      <CheckCheck className="w-3 h-3" /> Mark all read
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {(inbox.admin || []).length === 0 && (
                    <div className="text-xs text-[#8A92A0]">No admin alerts.</div>
                  )}
                  {(inbox.admin || []).slice(0, 30).map((r) => (
                    <div
                      key={r.inmail_id || r.subject + r.created_at}
                      className={`rounded-lg border px-3 py-2 text-sm ${priorityColor(r.priority)} ${
                        r.read ? "opacity-60" : ""
                      }`}
                      data-testid="notification-admin-row"
                    >
                      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide font-bold mb-1">
                        {kindIcon(r.kind)}
                        {r.kind || "alert"}
                        <span className="ml-auto text-[10px] text-[#8A92A0]">
                          {timeAgo(r.created_at)}
                        </span>
                      </div>
                      <div className="font-semibold text-white text-xs truncate">
                        {r.subject}
                      </div>
                      {r.body && (
                        <div className="text-[11px] text-[#c0c9d4] whitespace-pre-wrap mt-1 max-h-24 overflow-y-auto">
                          {r.body}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-[#EC008C]" />
                  <span className="uppercase text-xs tracking-widest text-[#EC008C] font-bold">
                    You ({inbox?.personal_unread || 0} unread)
                  </span>
                </div>
                {(inbox?.personal_unread || 0) > 0 && (
                  <button
                    type="button"
                    onClick={() => markAll("personal")}
                    className="text-xs text-[#8A92A0] hover:text-white flex items-center gap-1"
                    data-testid="notification-markall-personal"
                  >
                    <CheckCheck className="w-3 h-3" /> Mark all read
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {(inbox?.personal || []).length === 0 && (
                  <div className="text-xs text-[#8A92A0]">Nothing here yet.</div>
                )}
                {(inbox?.personal || []).slice(0, 30).map((r) => (
                  <div
                    key={r.notification_id || r.title + r.created_at}
                    className={`rounded-lg border border-[#1A2030] bg-[#0A0E18] px-3 py-2 text-sm ${
                      r.read ? "opacity-60" : ""
                    }`}
                    data-testid="notification-personal-row"
                  >
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide font-bold mb-1 text-[#EC008C]">
                      <Eye className="w-3 h-3" />
                      {r.notification_type || "update"}
                      <span className="ml-auto text-[10px] text-[#8A92A0]">
                        {timeAgo(r.created_at)}
                      </span>
                    </div>
                    <div className="font-semibold text-white text-xs">{r.title}</div>
                    {r.message && (
                      <div className="text-[11px] text-[#c0c9d4] mt-1">{r.message}</div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </aside>
        </div>
      )}
    </>
  );
};

export default NotificationBell;
